/** 患者フォーム・一覧で共通利用（バックエンド CHECK / 定数と一致させる） */

import type { MedicalDevices } from './patientMedicalDevices';

export type PatientStaffRef = {
  id: number;
  display_name: string;
};

export type PatientAssignee = {
  id: number;
  display_name: string;
  avatar_url: string | null;
  role?: string;
};

export type PatientPhysicianRef = {
  id: number;
  display_name: string;
  avatar_url?: string | null;
};

export type PatientMedicalOrganization = {
  id: number;
  name: string;
  address: string | null;
  default_phone: string | null;
};

export const PATIENT_STATUS_VALUES = ['draft', 'new', 'chart_registered'] as const;
export type PatientStatus = (typeof PATIENT_STATUS_VALUES)[number];

export const PATIENT_ACTION_STATUS_VALUES = ['none', 'needs_action', 'waiting'] as const;
export type PatientActionStatus = (typeof PATIENT_ACTION_STATUS_VALUES)[number];

export const CARE_INSURANCE_STATUS_VALUES = [
  'not_applied',
  'new_application',
  'level_change',
  'certified',
] as const;
export type CareInsuranceStatus = (typeof CARE_INSURANCE_STATUS_VALUES)[number];

export const CARE_INSURANCE_LEVEL_VALUES = [
  'support_1',
  'support_2',
  'care_1',
  'care_2',
  'care_3',
  'care_4',
  'care_5',
] as const;
export type CareInsuranceLevel = (typeof CARE_INSURANCE_LEVEL_VALUES)[number];

export const MEDICAL_INSURANCE_TYPE_VALUES = [
  'social_insurance',
  'national_health',
  'elderly_70_74',
  'late_stage_75',
  'public_assistance',
] as const;
export type MedicalInsuranceType = (typeof MEDICAL_INSURANCE_TYPE_VALUES)[number];

export const MEDICAL_INSURANCE_INCOME_CLASS_VALUES = [
  'income_a',
  'income_i',
  'income_u',
  'income_e',
  'income_o',
] as const;
export type MedicalInsuranceIncomeClass = (typeof MEDICAL_INSURANCE_INCOME_CLASS_VALUES)[number];

export const MEDICAL_INSURANCE_COPAY_VALUES = ['copay_10', 'copay_20', 'copay_30'] as const;
export type MedicalInsuranceCopay = (typeof MEDICAL_INSURANCE_COPAY_VALUES)[number];

export const INTRACTABLE_DISEASE_CAP_VALUES = [
  'cap_0',
  'cap_1000',
  'cap_2500',
  'cap_5000',
  'cap_10000',
  'cap_20000',
  'cap_30000',
] as const;
export type IntractableDiseaseCap = (typeof INTRACTABLE_DISEASE_CAP_VALUES)[number];

export const DISABILITY_MEDICAL_CERT_VALUES = ['osaka_in', 'osaka_out'] as const;
export type DisabilityMedicalCert = (typeof DISABILITY_MEDICAL_CERT_VALUES)[number];

export const PHARMACY_STATUS_VALUES = ['none', 'has'] as const;
export type PharmacyStatus = (typeof PHARMACY_STATUS_VALUES)[number];

export const REQUEST_STATUS_VALUES = ['pending', 'done'] as const;
export type RequestStatus = (typeof REQUEST_STATUS_VALUES)[number];

export const MEDICAL_INFO_REQUEST_STATUS_VALUES = [
  'not_requested',
  'in_progress',
  'received',
] as const;
export type MedicalInfoRequestStatus = (typeof MEDICAL_INFO_REQUEST_STATUS_VALUES)[number];

export type MedicalInfoRequestItem = {
  destination: string;
  status: MedicalInfoRequestStatus;
};

export type VisitNursingInfo = {
  has_service?: boolean;
  office_name?: string;
  frequency?: string;
  instruction_source?: string;
  period_start?: string;
  period_end?: string;
};

export type OtherCareServices = {
  visit_rehab?: 'yes' | 'no';
  day_service?: 'yes' | 'no';
  helper?: 'yes' | 'no';
  other?: 'yes' | 'no';
  visit_rehab_note?: string;
  day_service_note?: string;
  helper_note?: string;
  other_note?: string;
  general_note?: string;
};

export type Patient = {
  id: number;
  name: string;
  name_kana: string | null;
  date_of_birth: string | null;
  gender: string | null;
  patient_number: string | null;
  created_at: string;
  status: PatientStatus;
  /** 現在の対応ステータス（下書き/受付完了とは別） */
  action_status: PatientActionStatus;
  /** 対応期日（YYYY-MM-DD、未設定は null） */
  action_due_date: string | null;
  archived_at: string | null;
  updated_at: string | null;
  notes: string;
  requested_on: string;
  visit_start_estimate: string | null;
  first_visit_date: string | null;
  first_visit_attendees: string | null;
  referral_source: PatientMedicalOrganization | null;
  referral_phone: string | null;
  referral_contact_name: string | null;
  request_background: string | null;
  family_doctor: PatientMedicalOrganization | null;
  address: string | null;
  patient_phone: string | null;
  parking_info: string | null;
  attending_physician: PatientPhysicianRef | null;
  diagnoses: string[];
  medical_devices: MedicalDevices;
  patient_condition: string | null;
  family_structure: string | null;
  key_person: string | null;
  key_person_phone: string | null;
  key_person_address: string | null;
  care_insurance_status: CareInsuranceStatus | null;
  care_insurance_level: CareInsuranceLevel | null;
  medical_insurance_type: MedicalInsuranceType | null;
  medical_insurance_income_class: MedicalInsuranceIncomeClass | null;
  medical_insurance_copay: MedicalInsuranceCopay | null;
  intractable_disease_cap: IntractableDiseaseCap | null;
  disability_medical_cert: DisabilityMedicalCert | null;
  home_care_intervention: boolean | null;
  home_care_contact_name: string | null;
  visit_nursing: VisitNursingInfo;
  other_care_services: OtherCareServices;
  pharmacy_status: PharmacyStatus | null;
  pharmacy_name: string | null;
  pharmacy_note: string | null;
  face_sheet_request: RequestStatus | null;
  care_plan_request: RequestStatus | null;
  medical_info_request: MedicalInfoRequestItem[];
  consent_appointment_date: string | null;
  consent_appointment_contact: string | null;
  /** 患者情報画面右側の院内フリーメモ（特記事項とは別） */
  staff_memo: string;
  extensions: Record<string, unknown>;
  assigned_to: PatientAssignee | null;
  created_by: PatientStaffRef | null;
  updated_by: PatientStaffRef | null;
};

export const patientStatusOptions: { label: string; value: PatientStatus }[] = [
  { label: '下書き', value: 'draft' },
  { label: '受付完了', value: 'new' },
  { label: 'カルテ登録済み', value: 'chart_registered' },
];

export const patientStatusLabels: Record<PatientStatus, string> = {
  draft: '下書き',
  new: '受付完了',
  chart_registered: 'カルテ登録済み',
};

export const patientActionStatusOptions: { label: string; value: PatientActionStatus }[] = [
  { label: 'なし', value: 'none' },
  { label: '要対応', value: 'needs_action' },
  { label: '対応待ち', value: 'waiting' },
];

export const patientActionStatusLabels: Record<PatientActionStatus, string> = {
  none: 'なし',
  needs_action: '要対応',
  waiting: '対応待ち',
};

/** 対応ステータス Chip の MUI color（一覧・詳細で共通） */
export type PatientActionStatusChipColor = 'default' | 'error' | 'warning';

export function patientActionStatusChipColor(
  actionStatus: PatientActionStatus
): PatientActionStatusChipColor {
  switch (actionStatus) {
    case 'needs_action':
      return 'error';
    case 'waiting':
      return 'warning';
    default:
      return 'default';
  }
}

/** ステータス Chip の MUI color（一覧・詳細で共通） */
export type PatientStatusChipColor = 'default' | 'success' | 'warning';

export function patientStatusChipColor(status: PatientStatus): PatientStatusChipColor {
  switch (status) {
    case 'draft':
      return 'default';
    case 'new':
      return 'success';
    case 'chart_registered':
      return 'warning';
    default:
      return 'default';
  }
}

/** 患者一覧カードのステータス表示（アーカイブ時は専用ラベル） */
export function patientListStatusDisplay(p: Pick<Patient, 'status' | 'archived_at'>): {
  label: string;
  chipColor: PatientStatusChipColor;
} {
  if (p.archived_at) {
    return { label: 'アーカイブ', chipColor: 'warning' };
  }
  return {
    label: patientStatusLabels[p.status],
    chipColor: patientStatusChipColor(p.status),
  };
}

/** 患者一覧・詳細で共通のステータス Chip 見た目 */
export const patientStatusChipSx = {
  height: 20,
  fontSize: '0.6875rem',
  fontWeight: 500,
  borderRadius: 0.75,
  '& .MuiChip-label': {
    px: 0.625,
    py: 0,
    lineHeight: 1.2,
  },
} as const;

export const PATIENT_NOTES_MAX_LENGTH = 10000;

/** 患者情報画面フリーメモの最大文字数 */
export const PATIENT_STAFF_MEMO_MAX_LENGTH = PATIENT_NOTES_MAX_LENGTH;

/** 新規入力用（男/女のみ） */
export const genderOptions = [
  { label: '男性', value: 'male' },
  { label: '女性', value: 'female' },
] as const;

export const genderLabels: Record<string, string> = {
  male: '男性',
  female: '女性',
  other: 'その他',
};

/** 未 / 済（書類依頼など） */
export const pendingDoneStatusOptions = [
  { label: '未', value: 'pending' as const },
  { label: '済', value: 'done' as const },
];

export const careInsuranceStatusOptions = [
  { label: '未申請', value: 'not_applied' as const },
  { label: '新規申請', value: 'new_application' as const },
  { label: '区分変更中', value: 'level_change' as const },
  { label: '認定あり', value: 'certified' as const },
];

export const medicalInsuranceTypeOptions = [
  { label: '社保', value: 'social_insurance' as const },
  { label: '国保', value: 'national_health' as const },
  { label: '高齢（70〜74歳）', value: 'elderly_70_74' as const },
  { label: '後期（75歳〜）', value: 'late_stage_75' as const },
  { label: '生活保護', value: 'public_assistance' as const },
];

export const medicalInsuranceIncomeClassOptions = [
  { label: 'ア', value: 'income_a' as const },
  { label: 'イ', value: 'income_i' as const },
  { label: 'ウ', value: 'income_u' as const },
  { label: 'エ', value: 'income_e' as const },
  { label: 'オ', value: 'income_o' as const },
];

export const medicalInsuranceCopayOptions = [
  { label: '1割', value: 'copay_10' as const },
  { label: '2割', value: 'copay_20' as const },
  { label: '3割', value: 'copay_30' as const },
];

export const intractableDiseaseCapOptions = [
  { label: '0円', value: 'cap_0' as const },
  { label: '1,000円', value: 'cap_1000' as const },
  { label: '2,500円', value: 'cap_2500' as const },
  { label: '5,000円', value: 'cap_5000' as const },
  { label: '10,000円', value: 'cap_10000' as const },
  { label: '20,000円', value: 'cap_20000' as const },
  { label: '30,000円', value: 'cap_30000' as const },
];

export const disabilityMedicalCertOptions = [
  { label: '大阪府内', value: 'osaka_in' as const },
  { label: '大阪府外', value: 'osaka_out' as const },
];

export const careInsuranceLevelOptions = [
  { label: '要支援1', value: 'support_1' as const },
  { label: '要支援2', value: 'support_2' as const },
  { label: '要介護1', value: 'care_1' as const },
  { label: '要介護2', value: 'care_2' as const },
  { label: '要介護3', value: 'care_3' as const },
  { label: '要介護4', value: 'care_4' as const },
  { label: '要介護5', value: 'care_5' as const },
];

export const pharmacyStatusOptions = [
  { label: 'なし（訪問薬剤なし）', value: 'none' as const },
  { label: 'あり（訪問薬剤あり）', value: 'has' as const },
];

/** @deprecated pendingDoneStatusOptions を使用 */
export const requestStatusOptions = pendingDoneStatusOptions;

export const medicalInfoRequestStatusOptions = [
  { label: '未依頼', value: 'not_requested' as const },
  { label: '依頼中', value: 'in_progress' as const },
  { label: '到着済', value: 'received' as const },
];

/** 診情依頼の依頼状況テキスト色 */
export function medicalInfoRequestStatusSxColor(status: MedicalInfoRequestStatus): string {
  if (status === 'not_requested') return 'error.main';
  if (status === 'in_progress') return 'info.main';
  return 'success.main';
}

export const yesNoOptions = [
  { label: 'あり', value: 'yes' as const },
  { label: 'なし', value: 'no' as const },
];

/** フォーム state → API ペイロード */
export function buildPatientPatchPayload(form: PatientIntakeFormState): Record<string, unknown> {
  return {
    requested_on: form.requestedOn,
    visit_start_estimate: form.visitStartEstimate || null,
    first_visit_date: form.firstVisitDate || null,
    first_visit_attendees: form.firstVisitAttendees || null,
    referral_source_id: form.referralSource?.id ?? null,
    referral_phone: form.referralPhone || null,
    referral_contact_name: form.referralContactName || null,
    request_background: form.requestBackground || null,
    family_doctor_org_id: form.familyDoctor?.id ?? null,
    patient_number: form.patientNumber || null,
    name: form.name || null,
    name_kana: form.nameKana || null,
    date_of_birth: form.dateOfBirth || null,
    gender: form.gender || null,
    address: form.address || null,
    patient_phone: form.patientPhone || null,
    parking_info: form.parkingInfo || null,
    attending_physician_staff_id: form.attendingPhysician?.id ?? null,
    diagnoses: form.diagnoses.filter((d) => d.trim() !== ''),
    medical_devices: form.medicalDevices,
    patient_condition: form.patientCondition || null,
    family_structure: form.familyStructure || null,
    key_person: form.keyPerson || null,
    key_person_phone: form.keyPersonPhone || null,
    key_person_address: form.keyPersonAddress || null,
    care_insurance_status: form.careInsuranceStatus || null,
    care_insurance_level:
      form.careInsuranceStatus === 'certified' ? form.careInsuranceLevel || null : null,
    medical_insurance_type: form.medicalInsuranceType || null,
    medical_insurance_income_class: form.medicalInsuranceIncomeClass || null,
    medical_insurance_copay: form.medicalInsuranceCopay || null,
    intractable_disease_cap: form.intractableDiseaseCap || null,
    disability_medical_cert: form.disabilityMedicalCert || null,
    home_care_intervention: form.homeCareIntervention,
    home_care_contact_name:
      form.homeCareIntervention === true ? form.homeCareContactName || null : null,
    visit_nursing: form.visitNursing,
    other_care_services: form.otherCareServices,
    pharmacy_status: form.pharmacyStatus || null,
    pharmacy_name: form.pharmacyStatus === 'has' ? form.pharmacyName || null : null,
    pharmacy_note: form.pharmacyNote || null,
    face_sheet_request: form.faceSheetRequest || null,
    care_plan_request: form.carePlanRequest || null,
    medical_info_request: form.medicalInfoRequests.filter((item) => item.destination.trim() !== ''),
    consent_appointment_date: form.consentAppointmentDate || null,
    consent_appointment_contact: form.consentAppointmentContact || null,
    notes: form.notes,
    assigned_staff_id: form.assignedStaff?.id ?? null,
  };
}

export type PatientIntakeFormState = {
  requestedOn: string;
  visitStartEstimate: string;
  firstVisitDate: string;
  firstVisitAttendees: string;
  referralSource: PatientMedicalOrganization | null;
  referralPhone: string;
  referralContactName: string;
  requestBackground: string;
  familyDoctor: PatientMedicalOrganization | null;
  patientNumber: string;
  name: string;
  nameKana: string;
  dateOfBirth: string;
  gender: string;
  address: string;
  patientPhone: string;
  parkingInfo: string;
  attendingPhysician: PatientAssignee | null;
  diagnoses: string[];
  medicalDevices: MedicalDevices;
  patientCondition: string;
  familyStructure: string;
  keyPerson: string;
  keyPersonPhone: string;
  keyPersonAddress: string;
  careInsuranceStatus: CareInsuranceStatus | '';
  careInsuranceLevel: CareInsuranceLevel | '';
  medicalInsuranceType: MedicalInsuranceType | '';
  medicalInsuranceIncomeClass: MedicalInsuranceIncomeClass | '';
  medicalInsuranceCopay: MedicalInsuranceCopay | '';
  intractableDiseaseCap: IntractableDiseaseCap | '';
  disabilityMedicalCert: DisabilityMedicalCert | '';
  homeCareIntervention: boolean | null;
  homeCareContactName: string;
  visitNursing: VisitNursingInfo;
  otherCareServices: OtherCareServices;
  pharmacyStatus: PharmacyStatus | '';
  pharmacyName: string;
  pharmacyNote: string;
  faceSheetRequest: RequestStatus | '';
  carePlanRequest: RequestStatus | '';
  medicalInfoRequests: MedicalInfoRequestItem[];
  consentAppointmentDate: string;
  consentAppointmentContact: string;
  notes: string;
  assignedStaff: PatientAssignee | null;
};

export function emptyIntakeFormState(): PatientIntakeFormState {
  const today = new Date().toISOString().slice(0, 10);
  return {
    requestedOn: today,
    visitStartEstimate: '',
    firstVisitDate: '',
    firstVisitAttendees: '',
    referralSource: null,
    referralPhone: '',
    referralContactName: '',
    requestBackground: '',
    familyDoctor: null,
    patientNumber: '',
    name: '',
    nameKana: '',
    dateOfBirth: '',
    gender: '',
    address: '',
    patientPhone: '',
    parkingInfo: '',
    attendingPhysician: null,
    diagnoses: [''],
    medicalDevices: {},
    patientCondition: '',
    familyStructure: '',
    keyPerson: '',
    keyPersonPhone: '',
    keyPersonAddress: '',
    careInsuranceStatus: '',
    careInsuranceLevel: '',
    medicalInsuranceType: '',
    medicalInsuranceIncomeClass: '',
    medicalInsuranceCopay: '',
    intractableDiseaseCap: '',
    disabilityMedicalCert: '',
    homeCareIntervention: null,
    homeCareContactName: '',
    visitNursing: {},
    otherCareServices: {},
    pharmacyStatus: '',
    pharmacyName: '',
    pharmacyNote: '',
    faceSheetRequest: '',
    carePlanRequest: '',
    medicalInfoRequests: [{ destination: '', status: 'not_requested' }],
    consentAppointmentDate: '',
    consentAppointmentContact: '',
    notes: '',
    assignedStaff: null,
  };
}

export function patientToFormState(p: Patient): PatientIntakeFormState {
  return {
    requestedOn: p.requested_on?.slice(0, 10) ?? new Date().toISOString().slice(0, 10),
    visitStartEstimate: p.visit_start_estimate ?? '',
    firstVisitDate: p.first_visit_date?.slice(0, 10) ?? '',
    firstVisitAttendees: p.first_visit_attendees ?? '',
    referralSource: p.referral_source,
    referralPhone: p.referral_phone ?? '',
    referralContactName: p.referral_contact_name ?? '',
    requestBackground: p.request_background ?? '',
    familyDoctor: p.family_doctor,
    patientNumber: p.patient_number ?? '',
    name: p.name ?? '',
    nameKana: p.name_kana ?? '',
    dateOfBirth: p.date_of_birth ?? '',
    gender: p.gender ?? '',
    address: p.address ?? '',
    patientPhone: p.patient_phone ?? '',
    parkingInfo: p.parking_info ?? '',
    attendingPhysician: p.attending_physician
      ? {
          id: p.attending_physician.id,
          display_name: p.attending_physician.display_name,
          avatar_url: p.attending_physician.avatar_url ?? null,
        }
      : null,
    diagnoses: p.diagnoses?.length ? [...p.diagnoses] : [''],
    medicalDevices: p.medical_devices ?? {},
    patientCondition: p.patient_condition ?? '',
    familyStructure: p.family_structure ?? '',
    keyPerson: p.key_person ?? '',
    keyPersonPhone: p.key_person_phone ?? '',
    keyPersonAddress: p.key_person_address ?? '',
    careInsuranceStatus: p.care_insurance_status ?? '',
    careInsuranceLevel: p.care_insurance_level ?? '',
    medicalInsuranceType: p.medical_insurance_type ?? '',
    medicalInsuranceIncomeClass: p.medical_insurance_income_class ?? '',
    medicalInsuranceCopay: p.medical_insurance_copay ?? '',
    intractableDiseaseCap: p.intractable_disease_cap ?? '',
    disabilityMedicalCert: p.disability_medical_cert ?? '',
    homeCareIntervention: p.home_care_intervention,
    homeCareContactName: p.home_care_contact_name ?? '',
    visitNursing: p.visit_nursing ?? {},
    otherCareServices: p.other_care_services ?? {},
    pharmacyStatus: p.pharmacy_status ?? '',
    pharmacyName: p.pharmacy_name ?? '',
    pharmacyNote: p.pharmacy_note ?? '',
    faceSheetRequest: p.face_sheet_request ?? '',
    carePlanRequest: p.care_plan_request ?? '',
    medicalInfoRequests: p.medical_info_request?.length
      ? p.medical_info_request.map((item) => ({ ...item }))
      : [{ destination: '', status: 'not_requested' as const }],
    consentAppointmentDate: p.consent_appointment_date?.slice(0, 10) ?? '',
    consentAppointmentContact: p.consent_appointment_contact ?? '',
    notes: p.notes ?? '',
    assignedStaff: p.assigned_to,
  };
}
